/** Funkcje wywołujące konkretne endpointy backendu. */

import { api } from './client'
import { tokenStorage } from './tokenStorage'
import type {
  AppModule,
  Attendance,
  AttendanceDay,
  AttendanceInput,
  AvailabilitySlot,
  BillingPeriod,
  CancellationReason,
  Feedback,
  FeedbackInput,
  FeedbackKind,
  FeedbackPatch,
  FeedbackStatus,
  Instrument,
  InstrumentInput,
  Lesson,
  LessonInput,
  LessonPatch,
  LessonSeries,
  Location,
  LocationInput,
  LoginResponse,
  MakeupCandidate,
  MessageResponse,
  ModuleSlug,
  MonthlyClosure,
  Page,
  PayrollReport,
  Room,
  RoomBooking,
  RoomBookingInput,
  RoomInput,
  ScheduleOverview,
  SeriesCancelResult,
  SeriesCreateResult,
  SeriesPurgeResult,
  SeriesInput,
  Student,
  StudentInput,
  StudentBreak,
  StudentBreakInput,
  StudentListItem,
  StudentNote,
  StudentStatus,
  StudentSummary,
  TeacherListItem,
  TeacherProfile,
  TeacherProfileInput,
  TeacherSummary,
  User,
  UserInput,
  UserRole,
  WeekOverview,
} from './types'

// --------------------------------------------------------------- uwierzytelnianie

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const data = await api.post<LoginResponse>(
      '/auth/login',
      { email, password },
      { skipAuthRefresh: true },
    )
    tokenStorage.save(data)
    return data
  },

  me: () => api.get<User>('/auth/me'),

  async logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken()
    try {
      if (refreshToken) {
        await api.post<MessageResponse>('/auth/logout', { refresh_token: refreshToken })
      }
    } finally {
      // Sesja lokalna znika nawet gdy serwer nie odpowie — użytkownik
      // klikający „Wyloguj” musi zostać wylogowany bezwarunkowo.
      tokenStorage.clear()
    }
  },

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<MessageResponse>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  config: () =>
    api.get<{ password_min_length: number; access_token_expire_minutes: number }>('/auth/config'),
}

// --------------------------------------------------------------- oddziały

export interface LocationQuery {
  page?: number
  size?: number
  search?: string
  include_inactive?: boolean
}

export const locationsApi = {
  list: (query: LocationQuery = {}) => api.get<Page<Location>>('/locations', { ...query }),

  get: (id: string) => api.get<Location>(`/locations/${id}`),

  create: (payload: LocationInput) => api.post<Location>('/locations', payload),

  update: (id: string, payload: Partial<LocationInput>) =>
    api.patch<Location>(`/locations/${id}`, payload),

  deactivate: (id: string) => api.delete<Location>(`/locations/${id}`),
}

// --------------------------------------------------------------- konta

export interface UserQuery {
  page?: number
  size?: number
  search?: string
  role?: UserRole | ''
  location_id?: string
  include_inactive?: boolean
}

export const usersApi = {
  list: (query: UserQuery = {}) => api.get<Page<User>>('/users', { ...query }),

  get: (id: string) => api.get<User>(`/users/${id}`),

  create: (payload: UserInput) => api.post<User>('/users', payload),

  update: (id: string, payload: Partial<UserInput>) => api.patch<User>(`/users/${id}`, payload),

  deactivate: (id: string) => api.delete<User>(`/users/${id}`),

  resetPassword: (id: string, newPassword: string, mustChangePassword = true) =>
    api.post<MessageResponse>(`/users/${id}/reset-password`, {
      new_password: newPassword,
      must_change_password: mustChangePassword,
    }),
}

// --------------------------------------------------------------- instrumenty

export const instrumentsApi = {
  list: (includeInactive = false) =>
    api.get<Instrument[]>('/instruments', { include_inactive: includeInactive }),

  create: (payload: InstrumentInput) => api.post<Instrument>('/instruments', payload),

  update: (id: string, payload: Partial<InstrumentInput>) =>
    api.patch<Instrument>(`/instruments/${id}`, payload),

  deactivate: (id: string) => api.delete<Instrument>(`/instruments/${id}`),
}

// --------------------------------------------------------------- sale

export interface RoomQuery {
  page?: number
  size?: number
  search?: string
  location_id?: string
  include_inactive?: boolean
}

export const roomsApi = {
  list: (query: RoomQuery = {}) => api.get<Page<Room>>('/rooms', { ...query }),

  get: (id: string) => api.get<Room>(`/rooms/${id}`),

  create: (payload: RoomInput & { location_id: string }) => api.post<Room>('/rooms', payload),

  update: (id: string, payload: Partial<RoomInput>) => api.patch<Room>(`/rooms/${id}`, payload),

  deactivate: (id: string) => api.delete<Room>(`/rooms/${id}`),
}

// --------------------------------------------------------------- nauczyciele

export interface TeacherQuery {
  page?: number
  size?: number
  search?: string
  instrument_id?: string
  location_id?: string
  accepting_only?: boolean
  include_inactive?: boolean
}

export const teachersApi = {
  list: (query: TeacherQuery = {}) => api.get<Page<TeacherListItem>>('/teachers', { ...query }),

  get: (userId: string) => api.get<TeacherProfile>(`/teachers/${userId}`),

  update: (userId: string, payload: TeacherProfileInput) =>
    api.patch<TeacherProfile>(`/teachers/${userId}`, payload),

  replaceAvailability: (userId: string, slots: Omit<AvailabilitySlot, 'id'>[]) =>
    api.put<TeacherProfile>(`/teachers/${userId}/availability`, { slots }),

  students: (userId: string, includeInactive = false) =>
    api.get<StudentListItem[]>(`/teachers/${userId}/students`, {
      include_inactive: includeInactive,
    }),
}

// --------------------------------------------------------------- uczniowie

export interface StudentQuery {
  page?: number
  size?: number
  search?: string
  location_id?: string
  instrument_id?: string
  teacher_id?: string
  status?: StudentStatus | ''
  include_archived?: boolean
}

export const studentsApi = {
  list: (query: StudentQuery = {}) => api.get<Page<StudentListItem>>('/students', { ...query }),

  get: (id: string) => api.get<Student>(`/students/${id}`),

  create: (payload: StudentInput & { location_id: string }) =>
    api.post<Student>('/students', payload),

  update: (id: string, payload: Partial<StudentInput>) =>
    api.patch<Student>(`/students/${id}`, payload),

  archive: (id: string) => api.delete<Student>(`/students/${id}`),

  notes: (id: string) => api.get<StudentNote[]>(`/students/${id}/notes`),

  addNote: (id: string, content: string) =>
    api.post<StudentNote>(`/students/${id}/notes`, { content }),

  deleteNote: (studentId: string, noteId: string) =>
    api.delete<MessageResponse>(`/students/${studentId}/notes/${noteId}`),

  /** Okres, w którym uczeń nie ma zajęć — blokuje rezerwacje w tym czasie. */
  addBreak: (studentId: string, payload: StudentBreakInput) =>
    api.post<StudentBreak>(`/students/${studentId}/breaks`, payload),

  deleteBreak: (studentId: string, breakId: string) =>
    api.delete<MessageResponse>(`/students/${studentId}/breaks/${breakId}`),
}

// --------------------------------------------------------------- grafik

export interface LessonQuery {
  date_from: string
  date_to: string
  location_id?: string
  room_id?: string
  teacher_id?: string
  student_id?: string
  include_cancelled?: boolean
}

export const roomBookingsApi = {
  list: (query: { date_from: string; date_to: string; room_id?: string }) =>
    api.get<RoomBooking[]>('/room-bookings', { ...query }),

  create: (payload: RoomBookingInput) => api.post<RoomBooking>('/room-bookings', payload),

  remove: (id: string) => api.delete<void>(`/room-bookings/${id}`),
}

export const scheduleApi = {
  lessons: (query: LessonQuery) => api.get<Lesson[]>('/schedule/lessons', { ...query }),

  lesson: (id: string) => api.get<Lesson>(`/schedule/lessons/${id}`),

  createLesson: (payload: LessonInput) => api.post<Lesson>('/schedule/lessons', payload),

  updateLesson: (id: string, payload: LessonPatch) =>
    api.patch<Lesson>(`/schedule/lessons/${id}`, payload),

  /** Kasuje zajęcia wpisane przez pomyłkę. Odbytych się nie usuwa. */
  deleteLesson: (id: string) => api.delete<void>(`/schedule/lessons/${id}`),

  cancelLesson: (id: string, reason: CancellationReason, note?: string) =>
    api.post<Lesson>(`/schedule/lessons/${id}/cancel`, { reason, note: note ?? null }),

  restoreLesson: (id: string) => api.post<Lesson>(`/schedule/lessons/${id}/restore`),

  series: (query: { location_id?: string; teacher_id?: string; include_inactive?: boolean } = {}) =>
    api.get<LessonSeries[]>('/schedule/series', { ...query }),

  createSeries: (payload: SeriesInput) => api.post<SeriesCreateResult>('/schedule/series', payload),

  /** Dane jednego cyklu — z nich formularz edycji wie, co dziś obowiązuje. */
  seriesDetail: (id: string) => api.get<LessonSeries>(`/schedule/series/${id}`),

  updateSeries: (id: string, payload: Partial<SeriesInput>) =>
    api.patch<SeriesCreateResult>(`/schedule/series/${id}`, payload),

  /** Odwołuje przyszłe terminy cyklu — zostają w historii wraz z powodem. */
  cancelSeries: (id: string, reason: CancellationReason, note?: string) =>
    api.post<SeriesCancelResult>(`/schedule/series/${id}/cancel`, { reason, note: note ?? null }),

  /** Kończy cykl: usuwa przyszłe terminy, historia zostaje nietknięta. */
  endSeries: (id: string) => api.delete<LessonSeries>(`/schedule/series/${id}`),

  /** Kasuje cały cykl wpisany przez pomyłkę, także terminy z przeszłości. */
  purgeSeries: (id: string) => api.delete<SeriesPurgeResult>(`/schedule/series/${id}/all`),

  overview: (query: {
    date_from: string
    date_to: string
    location_id?: string
    teacher_id?: string
    room_id?: string
  }) => api.get<ScheduleOverview>('/schedule/overview', { ...query }),

  /** Ogólny grafik tygodniowy — kto i w jakich godzinach pracuje. */
  weekOverview: (query: {
    date_from: string
    date_to: string
    location_id?: string
    room_id?: string
  }) => api.get<WeekOverview>('/schedule/week-overview', { ...query }),
}

// --------------------------------------------------------------- obecności

export const attendanceApi = {
  /** Komplet zajęć dnia wraz z licznikiem uzupełnionych wpisów. */
  day: (day: string, teacherId?: string) =>
    api.get<AttendanceDay>('/attendance/day', { day, teacher_id: teacherId }),

  /** Zajęcia, które się odbyły, a nie zostały rozliczone. */
  pending: (query: { teacher_id?: string; limit?: number } = {}) =>
    api.get<Lesson[]>('/attendance/pending', { ...query }),

  makeups: (query: { teacher_id?: string; student_id?: string; only_outstanding?: boolean } = {}) =>
    api.get<MakeupCandidate[]>('/attendance/makeups', { ...query }),

  /** Idempotentne: powtórzone wywołanie nadpisuje wpis zamiast tworzyć drugi. */
  set: (lessonId: string, payload: AttendanceInput) =>
    api.put<Attendance>(`/attendance/lessons/${lessonId}`, payload),

  clear: (lessonId: string) => api.delete<void>(`/attendance/lessons/${lessonId}`),
}

// --------------------------------------------------------------- rozliczenia

export const payrollApi = {
  /** Semestry, wakacje i rok szkolny — definiowane po stronie serwera. */
  periods: (schoolYear?: number) =>
    api.get<BillingPeriod[]>('/payroll/periods', { school_year: schoolYear }),

  summary: (query: { date_from: string; date_to: string; teacher_id?: string }) =>
    api.get<TeacherSummary>('/payroll/summary', { ...query }),

  studentSummary: (studentId: string, query: { date_from: string; date_to: string }) =>
    api.get<StudentSummary>(`/payroll/students/${studentId}/summary`, { ...query }),

  closures: (teacherId?: string) =>
    api.get<MonthlyClosure[]>('/payroll/closures', { teacher_id: teacherId }),

  closeMonth: (payload: { year: number; month: number; teacher_id?: string; note?: string }) =>
    api.post<MonthlyClosure>('/payroll/closures', payload),

  reopenMonth: (year: number, month: number, teacherId: string) =>
    api.delete<void>(`/payroll/closures/${year}/${month}?teacher_id=${teacherId}`),

  report: (year: number, month: number) =>
    api.get<PayrollReport>('/payroll/report', { year, month }),
}

// --------------------------------------------------------------- zgłoszenia

export const feedbackApi = {
  /** Wysyła zgłoszenie. Dostępne dla każdego zalogowanego. */
  create: (payload: FeedbackInput) => api.post<Feedback>('/feedback', payload),

  /** Lista zgłoszeń — wyłącznie dla właścicielki. */
  list: (
    query: { page?: number; size?: number; status?: FeedbackStatus; kind?: FeedbackKind } = {},
  ) => api.get<Page<Feedback>>('/feedback', { ...query }),

  update: (id: string, payload: FeedbackPatch) => api.patch<Feedback>(`/feedback/${id}`, payload),
}

// --------------------------------------------------------------------- moduły

export const modulesApi = {
  /** Stan wszystkich modułów — z tego menu buduje swoją listę pozycji. */
  list: () => api.get<AppModule[]>('/modules'),

  setEnabled: (slug: ModuleSlug, isEnabled: boolean) =>
    api.patch<AppModule>(`/modules/${slug}`, { is_enabled: isEnabled }),
}
